import { sql } from "drizzle-orm"
import {
  boolean,
  check,
  date,
  decimal,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { companies } from "./company"
import { contacts, locations } from "./contact"
import { estates } from "./estate"
import { trusts } from "./trust"

export const assetTypeEnum = pgEnum("asset_type_enum", [
  "401B",
  "401K",
  "457B",
  "ART",
  "BANK_ACCOUNT",
  "BANK_ACCOUNT_OR_CASH",
  "BOAT",
  "BUSINESS_INTEREST",
  "CASH",
  "CASH_BEQUEST",
  "COMPANY_SHARES",
  "CORP_PENSION",
  "CPP",
  "CRYPTOCURRENCY",
  "CURRENCY",
  "DIGITAL_ASSETS",
  "DIGITAL_CURRENCY",
  "EQUIPMENT",
  "FINANCIAL_ACCOUNT",
  "GOV_PENSION",
  "HEIRLOOM",
  "INSURANCE",
  "INVESTMENT",
  "INVESTMENT_ACCOUNT",
  "IRA",
  "JEWELRY",
  "LIFE_INSURANCE",
  "LIRA",
  "OTHER",
  "PERSONAL_ITEMS",
  "PERSONAL_PROPERTY",
  "PRECIOUS_METALS",
  "PRIMARY_RESIDENCE",
  "REAL_ESTATE",
  "REAL_PROPERTY",
  "RENTAL_PROPERTY",
  "RESIDUARY_ESTATE",
  "RETIREMENT_ACCOUNT",
  "ROLLOVER_IRA",
  "ROTH_IRA",
  "RRIF",
  "RRSP",
  "SAVINGS",
  "SEP_IRA",
  "SIMPLE_IRA",
  "SOCIAL_SECURITY",
  "SOLO_401K",
  "STOCK_AND_BONDS",
  "STOCK_OPTIONS_OR_EQUITY",
  "TFSA",
  "TRUST",
  "TSP",
  "VEHICLE",
])

export const assetOwnershipTypeEnum = pgEnum("asset_ownership_type_enum", [
  "COMMUNITY_PROPERTY",
  "COMMUNITY_PROPERTY_WITH_SURVIVORSHIP",
  "COMPANY",
  "JOINT_TENANCY",
  "JOINT_TENANCY_WITH_SPOUSE",
  "LIFE_ESTATE",
  "SOLE_OWNERSHIP",
  "TENANCY_IN_COMMON",
  "TENANCY_IN_COMMON_WITH_SPOUSE",
  "TOD_POD",
  "TRUST",
])

export const liabilityTypeEnum = pgEnum("liability_type_enum", [
  "BUSINESS_LOAN",
  "CONVERTABLE_DEBT",
  "LINE_OF_CREDIT",
  "MORTGAGE",
])

// Core Tables

export const assetOwners = pgTable(
  "asset_owner",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    assetId: uuid("asset_id")
      .notNull()
      .references(() => assets.id, { onDelete: "cascade" }),
    contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
    companyId: uuid("company_id").references(() => companies.id, { onDelete: "cascade" }),
    trustId: uuid("trust_id").references(() => trusts.id, { onDelete: "cascade" }),
    assetOwnershipPercentage: decimal("asset_ownership_percentage", { precision: 5, scale: 2 })
      .default("100")
      .notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => ({
    oneOwnerChk: check(
      "asset_owner_one_owner_chk",
      sql`(
        (CASE WHEN ${table.contactId} IS NOT NULL THEN 1 ELSE 0 END) +
        (CASE WHEN ${table.companyId} IS NOT NULL THEN 1 ELSE 0 END) +
        (CASE WHEN ${table.trustId}   IS NOT NULL THEN 1 ELSE 0 END)
      ) = 1`,
    ),
  }),
)

export const assetContacts = pgTable("asset_contact", {
  id: uuid("id").primaryKey().defaultRandom(),
  assetId: uuid("asset_id")
    .notNull()
    .references(() => assets.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores information about assets belonging to an estate.
export const assets = pgTable("asset", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id")
    .notNull()
    .references(() => estates.id, { onDelete: "cascade" }),
  assetType: assetTypeEnum("asset_type_enum").notNull(),
  assetOwnershipType: assetOwnershipTypeEnum("asset_ownership_type_enum").notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  currentValue: decimal("current_value", { precision: 18, scale: 2 }),
  averageCostBasis: decimal("average_cost_basis", { precision: 18, scale: 2 }),
  acquisitionDate: date("acquisition_date"),
  isLiquidatable: boolean("is_liquidatable").default(true),
  isPrimaryResidence: boolean("is_primary_residence").default(false),
  locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
  accountNumber: varchar("account_number", { length: 255 }),
  assetLiabilityId: uuid("asset_liability_id").references(() => liabilities.id, {
    onDelete: "set null",
  }),
  revenue: decimal("revenue", { precision: 18, scale: 2 }),
  expenses: decimal("expenses", { precision: 18, scale: 2 }),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores information about liabilities associated with an estate.
export const liabilities = pgTable("liability", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id")
    .notNull()
    .references(() => estates.id, { onDelete: "cascade" }),
  liabilityType: liabilityTypeEnum("liability_type_enum").notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  currentBalance: decimal("current_balance", { precision: 18, scale: 2 }),
  creditorContactId: uuid("creditor_contact_id").references(() => contacts.id, {
    onDelete: "set null",
  }),
  locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
  dueDate: date("due_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Projection Tables

// Purpose: Projects future values of specific assets.
export const assetProjections = pgTable("asset_projection", {
  id: uuid("id").primaryKey().defaultRandom(),
  assetId: uuid("asset_id")
    .notNull()
    .references(() => assets.id, { onDelete: "cascade" }),
  projectionDate: date("projection_date").notNull(),
  projectedValue: decimal("projected_value", {
    precision: 18,
    scale: 2,
  }).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Projects future expenses for an estate.
export const expenseProjections = pgTable("expense_projection", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id")
    .notNull()
    .references(() => estates.id, { onDelete: "cascade" }),
  projectionDate: date("projection_date").notNull(),
  amount: decimal("amount", { precision: 18, scale: 2 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Projects future values or balances of liabilities.
export const liabilityProjections = pgTable("liability_projection", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id")
    .notNull()
    .references(() => estates.id, { onDelete: "cascade" }),
  liabilityId: uuid("liability_id").references(() => liabilities.id, { onDelete: "cascade" }),
  projectionDate: date("projection_date").notNull(),
  projectedBalance: decimal("projected_balance", {
    precision: 18,
    scale: 2,
  }).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Projects future revenue for an estate.
export const revenueProjections = pgTable("revenue_projection", {
  id: uuid("id").primaryKey().defaultRandom(),
  estateId: uuid("estate_id")
    .notNull()
    .references(() => estates.id, { onDelete: "cascade" }),
  projectionDate: date("projection_date").notNull(),
  amount: decimal("amount", { precision: 18, scale: 2 }).notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})
